import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

interface SharedFile {
  file_id: string;
  filename: string;
  size: number;
  mime_type: string;
}

interface ShareLink {
  linkId: string;
  clientName: string;
  files: SharedFile[];
  expiresAt: string;
  downloadsRemaining: number;
  requiresPassword: boolean;
}

export const ShareAccessPortal: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const [shareLink, setShareLink] = useState<ShareLink | null>(null);
  const [password, setPassword] = useState('');
  const [downloadTokens, setDownloadTokens] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (linkId) {
      fetchShareLink();
    }
  }, [linkId]);

  const fetchShareLink = async () => {
    try {
      const response = await fetch(`http://localhost:8787/api/share/${linkId}`);
      const data = await response.json();

      if (data.success) {
        setShareLink(data.shareLink);
        if (!data.shareLink.requiresPassword) {
          // Auto-verify if no password required
          await verifyAccess();
        }
      } else {
        setError(data.error);
      }
    } catch (error) {
      setError('Failed to load share link');
    } finally {
      setLoading(false);
    }
  };

  const verifyAccess = async (withPassword?: string) => {
    try {
      setLoading(true);
      const response = await fetch(`http://localhost:8787/api/share/${linkId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: withPassword }),
      });

      const data = await response.json();

      if (data.success) {
        const tokens: Record<string, string> = {};
        data.downloadTokens.forEach((dt: any) => {
          tokens[dt.fileId] = dt.token;
        });
        setDownloadTokens(tokens);
        setPasswordVerified(true);
      } else {
        setError(data.error);
      }
    } catch (error) {
      setError('Failed to verify access');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password) {
      verifyAccess(password);
    }
  };

  const downloadFile = async (fileId: string, filename: string) => {
    const token = downloadTokens[fileId];
    if (!token) return;

    setDownloadingFiles(prev => new Set(prev).add(fileId));

    try {
      const response = await fetch(`http://localhost:8787/api/share/download/${token}`);

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Refresh share link to update download count
      await fetchShareLink();
    } catch (error) {
      alert('Failed to download file');
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  };

  const downloadAllFiles = async () => {
    for (const file of shareLink?.files || []) {
      await downloadFile(file.file_id, file.filename);
      // Small delay between downloads
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  };

  const formatExpiry = (expiresAt: string): string => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();

    if (diff < 0) return 'Expired';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''}, ${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-pulse">⏳</div>
          <p className="text-xl">Loading share link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!shareLink) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-2xl font-bold mb-2">Share Link Not Found</h1>
          <p className="text-gray-400">This share link does not exist or has been removed.</p>
        </div>
      </div>
    );
  }

  if (shareLink.requiresPassword && !passwordVerified) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="bg-gray-800 rounded-lg p-8">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🔒</div>
              <h1 className="text-2xl font-bold mb-2">Password Protected</h1>
              <p className="text-gray-400">This share link requires a password</p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full px-4 py-3 bg-gray-900 rounded border border-gray-700 text-white"
                autoFocus
              />
              <button
                type="submit"
                disabled={!password}
                className="w-full bg-blue-600 py-3 rounded font-bold hover:bg-blue-700 disabled:opacity-50"
              >
                Access Files
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">📁 Secure File Access</h1>
              <p className="text-white/80">Files shared by {shareLink.clientName}</p>
            </div>
            <div className="text-right">
              <div className="text-6xl">🔐</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="bg-white/10 rounded p-3">
              <p className="text-sm text-white/60">Expires In</p>
              <p className="font-bold">{formatExpiry(shareLink.expiresAt)}</p>
            </div>
            <div className="bg-white/10 rounded p-3">
              <p className="text-sm text-white/60">Downloads Remaining</p>
              <p className="font-bold">{shareLink.downloadsRemaining}</p>
            </div>
          </div>
        </div>

        {/* File List */}
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Shared Files ({shareLink.files.length})</h2>
            {shareLink.files.length > 1 && (
              <button
                onClick={downloadAllFiles}
                className="px-4 py-2 bg-green-600 rounded font-bold hover:bg-green-700"
              >
                📦 Download All
              </button>
            )}
          </div>

          <div className="space-y-3">
            {shareLink.files.map((file) => (
              <div
                key={file.file_id}
                className="flex items-center justify-between p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-3xl">
                    {file.mime_type?.includes('pdf') ? '📄' :
                     file.mime_type?.includes('image') ? '🖼️' :
                     file.mime_type?.includes('video') ? '🎥' :
                     file.mime_type?.includes('audio') ? '🎵' :
                     file.mime_type?.includes('zip') ? '📦' : '📎'}
                  </div>
                  <div>
                    <p className="font-medium">{file.filename}</p>
                    <p className="text-sm text-gray-400">{formatFileSize(file.size)}</p>
                  </div>
                </div>

                <button
                  onClick={() => downloadFile(file.file_id, file.filename)}
                  disabled={downloadingFiles.has(file.file_id)}
                  className="px-4 py-2 bg-blue-600 rounded font-bold hover:bg-blue-700 disabled:opacity-50"
                >
                  {downloadingFiles.has(file.file_id) ? '⏳ Downloading...' : '⬇️ Download'}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-400 text-sm">
          <p>This is a secure file transfer. Files are encrypted and access is logged.</p>
          <p className="mt-2">Having issues? Contact the sender.</p>
        </div>
      </div>
    </div>
  );
};